import { useEffect, useMemo, useRef, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";

const StickyBootstrapTableWrapper = ({
  children, columns,
  table_wrapper_classes = 'sfTable-Container-for-creatives-campaigns-tabs',
  table_1_classes = 'sfTable-Container-for-creatives-campaigns-tab-1',
  table_2_classes = 'sfTable-Container-for-creatives-campaigns-tab-2', ...other_props
}) => {
  const sticky_table_ref = useRef(null);
  const non_sticky_table_ref = useRef(null); 
  const sticky_header_ref = useRef(null);
  const non_sticky_header_ref = useRef(null)

  const [scrolling, setScrolling] = useState(false);

  const isVisibleColumnPresent = (columns) => {
    const isPresent = columns?.reduce((a, c) => {
      if (c?.hidden) {
        return a || false
      } else {
        return true
      }
    }, false)
    return isPresent
  }

  const onScroll = (table_ref_src, table_ref_tgt) => {
    if (table_ref_src?.current && table_ref_tgt?.current) {
      table_ref_tgt.current.scrollTop = table_ref_src.current.scrollTop;
      // Limits the scroll to keep it equal always
      table_ref_src.current.scrollTop = table_ref_tgt.current.scrollTop;

      // Set scrolling to true while scrolling is in progress
      setScrolling(true);

      // After a short delay (e.g., 300ms), set scrolling back to false
      setTimeout(() => {
        setScrolling(false);
      }, 500);
    }
  }

  const [sortField, setSortField] = useState(other_props?.defaultSorted?.[0]?.dataField);
  const [sortOrder, setSortOrder] = useState(other_props?.defaultSorted?.[0]?.order);
  const [rowsHoverStates, setRowsHoverStates] = useState({});

  // Take any row class from the props and add hovered state
  // to it which we get from onmouseenter and onmouseleave
  const rowClasses = (_row, rowIndex) => {
    let propRowClasses = '';
    if (typeof other_props?.rowClasses === 'string') {
      propRowClasses = `${other_props.rowClasses} `;
    }

    if (rowsHoverStates[rowIndex]) {
      return propRowClasses + 'tr-hover-hovered';
    }
    return propRowClasses;
  }

  // Take any events from parent while adding its own behaviour for setting
  // hovered state
  const rowEvents = {
    ...other_props?.rowEvents,
    onMouseEnter: (e, row, rowIndex) => {
      if (other_props?.rowEvents?.onMouseEnter) {
        other_props.rowEvents.onMouseEnter(e, row, rowIndex);
      }
      setRowsHoverStates((prev) => ({ ...prev, [rowIndex]: true }));
    },
    onMouseLeave: (e, row, rowIndex) => {
      if (other_props?.rowEvents?.onMouseLeave) {
        other_props.rowEvents.onMouseLeave(e, row, rowIndex);
      }
      setRowsHoverStates((prev) => ({ ...prev, [rowIndex]: false }));
    },
  }

  const handleSort = (field, order) => {
    if (other_props?.setSortOrderForName && field === 'name') {
      other_props.setSortOrderForName(order);
    }
    setSortField(field);
    !!other_props?.setSortFieldForTable && other_props?.setSortFieldForTable(field, order)
    setSortOrder(order);
  };

  const sticky_columns = columns.map(column => ({ ...column, hidden: !column.sticky, onSort: handleSort }));
  const non_sticky_columns = columns.map(column => ({ ...column, hidden: !!column.sticky || column?.hidden, onSort: handleSort }));
  const tableSortObj = other_props?.sort;
  const finalSortObj = { dataField: sortField, order: sortOrder, ...tableSortObj };

  const checkSplitHeaderColPresent = (table_columns) => {
    return table_columns?.some((col) => col?.splitHeader == true && col?.hidden == false);
  }

  const useSplitHeaderStyleTable1 = useMemo(() => {
    return !checkSplitHeaderColPresent(sticky_columns) && checkSplitHeaderColPresent(non_sticky_columns)
  }, [columns])

  const useSplitHeaderStyleTable2 = useMemo(() => {
    return checkSplitHeaderColPresent(sticky_columns) && !checkSplitHeaderColPresent(non_sticky_columns)
  }, [columns])

  const splitHeaderStyle = {
    // marginTop: '13.5px' //We dont need this since this is handled in the headerClasses
  }

  // For calculating header height dynamically
  useEffect(() => {
    const adjustHeaderHeights = () => {
      const stickyContainer = document.querySelector('.sticky-table');
      const nonStickyContainer = document.querySelector('.non-sticky-table');

      if (stickyContainer && nonStickyContainer) {
        const stickyHeader = stickyContainer.querySelector('.sftableHeader');
        const nonStickyHeader = nonStickyContainer.querySelector('.sftableHeader');

        if (stickyHeader && nonStickyHeader) {
          const stickyHeaderHeight = stickyHeader.offsetHeight;
          const nonStickyHeaderHeight = nonStickyHeader.offsetHeight;
          const maxHeight = Math.max(stickyHeaderHeight, nonStickyHeaderHeight);

          // Apply the max height to both headers
          stickyHeader.style.height = `${maxHeight}px`;
          nonStickyHeader.style.height = `${maxHeight}px`;
        }
      }
    };

    adjustHeaderHeights();

    // Re-adjust on window resize
    window.addEventListener('resize', adjustHeaderHeights);
    return () => window.removeEventListener('resize', adjustHeaderHeights);
  }, [columns]);

  return (
    <div className={`${table_wrapper_classes}`}>
      <div style={{ display: "flex" }}>
        {isVisibleColumnPresent(sticky_columns) && <div
          ref={sticky_table_ref}
          className={`${table_1_classes} sticky-table ${scrolling ? 'disable-pointer-events' : ''}`}
          onScroll={() => { onScroll(sticky_table_ref, non_sticky_table_ref) }}
          style={useSplitHeaderStyleTable1 ? splitHeaderStyle: {}}
        >
          <div ref={sticky_header_ref}>
          <BootstrapTable
            {...other_props}
            columns={sticky_columns}
            rowClasses={rowClasses}
            rowEvents={rowEvents}
            sort={finalSortObj}
            noDataIndication={other_props?.noDataIndication}
          >
            {children}
          </BootstrapTable>
          </div>
        </div>}
        {isVisibleColumnPresent(non_sticky_columns) && <div
          ref={non_sticky_table_ref}
          style={{ flex: 1, ...(useSplitHeaderStyleTable2 ? splitHeaderStyle : {}) }}
          className={`${table_2_classes} non-sticky-table ${scrolling ? 'disable-pointer-events' : ''}`}
          onScroll={() => { onScroll(non_sticky_table_ref, sticky_table_ref) }}
        >
          <div ref={non_sticky_header_ref}>
          <BootstrapTable
            {...other_props}
            columns={non_sticky_columns}
            rowClasses={rowClasses}
            rowEvents={rowEvents}
            sort={finalSortObj}
            selectRow={undefined}
            noDataIndication={false}
          >
            {children}
          </BootstrapTable>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default StickyBootstrapTableWrapper;
